import type {
  EnvironmentId,
  PullRequestListEntry,
  PullRequestListProjectError,
} from "@cmmd-forge/contracts";

export type PullRequestInboxGroup = "reviewing" | "authored" | "other";

export interface PullRequestInboxRow extends PullRequestListEntry {
  readonly environmentId: EnvironmentId;
  readonly rowKey: string;
  readonly group: PullRequestInboxGroup;
}

export interface PullRequestInboxError {
  readonly environmentId: EnvironmentId;
  readonly message: string;
}

export interface PullRequestInboxGroupSection {
  readonly label: "Reviewing" | "Authored" | "Others";
  readonly rows: ReadonlyArray<PullRequestInboxRow>;
}

interface PullRequestEnvironmentInboxResult {
  readonly environmentId: EnvironmentId;
  readonly entries: ReadonlyArray<PullRequestListEntry>;
  readonly viewers: Readonly<Record<string, string>>;
  readonly responseErrors: ReadonlyArray<PullRequestListProjectError>;
  readonly isPending: boolean;
  readonly error: Error | null;
}

function inboxGroup(
  entry: PullRequestListEntry,
  viewers: Readonly<Record<string, string>>,
): PullRequestInboxGroup {
  const viewer = viewers[entry.host]?.toLowerCase();
  if (viewer !== undefined && entry.author?.login.toLowerCase() === viewer) return "authored";
  return entry.viewerReviewRequested ? "reviewing" : "other";
}

function inboxError(
  environmentId: EnvironmentId,
  error: PullRequestListProjectError,
): PullRequestInboxError {
  return { environmentId, message: `${error.projectTitle}: ${error.message}` };
}

function rowsForEnvironment(
  result: PullRequestEnvironmentInboxResult,
): ReadonlyArray<PullRequestInboxRow> {
  return result.entries.map((entry) => ({
    ...entry,
    environmentId: result.environmentId,
    rowKey: `${result.environmentId}:${entry.host}:${entry.repository}#${entry.number}`,
    group: inboxGroup(entry, result.viewers),
  }));
}

const GROUPS = [
  ["reviewing", "Reviewing"],
  ["authored", "Authored"],
  ["other", "Others"],
] as const satisfies ReadonlyArray<
  readonly [PullRequestInboxGroup, PullRequestInboxGroupSection["label"]]
>;

export function aggregatePullRequestInbox(
  results: ReadonlyArray<PullRequestEnvironmentInboxResult>,
): {
  readonly groups: ReadonlyArray<PullRequestInboxGroupSection>;
  readonly rows: ReadonlyArray<PullRequestInboxRow>;
  readonly errors: ReadonlyArray<PullRequestInboxError>;
  readonly isPending: boolean;
  readonly state: "loading" | "partial" | "fatal" | "ready";
} {
  const rows = uniqueRowsByIdentity(results.flatMap(rowsForEnvironment)).toSorted((left, right) =>
    right.updatedAt.localeCompare(left.updatedAt),
  );
  const errors = results.flatMap((result) => [
    ...result.responseErrors.map((error) => inboxError(result.environmentId, error)),
    ...(result.error === null
      ? []
      : [{ environmentId: result.environmentId, message: result.error.message }]),
  ]);
  const isPending = results.some((result) => result.isPending);
  const state =
    isPending && rows.length === 0
      ? "loading"
      : errors.length === 0
        ? "ready"
        : rows.length === 0
          ? "fatal"
          : "partial";
  return {
    groups: GROUPS.flatMap(([group, label]) => {
      const groupedRows = rows.filter((row) => row.group === group);
      return groupedRows.length === 0 ? [] : [{ label, rows: groupedRows }];
    }),
    rows,
    errors,
    isPending,
    state,
  };
}

function uniqueRowsByIdentity(
  rows: ReadonlyArray<PullRequestInboxRow>,
): ReadonlyArray<PullRequestInboxRow> {
  const unique = new Map<string, PullRequestInboxRow>();
  for (const row of rows) {
    if (!unique.has(row.rowKey)) unique.set(row.rowKey, row);
  }
  return [...unique.values()];
}
